import type {
  PlanId, NodeId, WallId, OpeningId, FurnitureId, RoomId, WifiSourceId,
} from '../ids.js'
import type { Mm, Vec2, Deg } from '../units.js'

/**
 * The floor plan is a PLANAR WALL GRAPH: nodes plus centreline edges.
 * All rendered geometry — wall polygons, miter joints, room outlines — is
 * DERIVED, never stored (docs/02 §6).
 */
export interface PlanNode {
  id: NodeId
  p: Vec2
}

export interface Wall {
  id: WallId
  a: NodeId
  b: NodeId
  thickness: Mm
  layer: string
  /** Structural walls attenuate wifi more and render heavier. */
  material: WallMaterial
}

export type WallMaterial = 'partition' | 'brick' | 'concrete' | 'glass' | 'wood'

export type OpeningKind = 'door' | 'double_door' | 'sliding_door' | 'window' | 'archway'

/**
 * Openings are HOSTED on a wall: parametrised by distance along it, so they
 * move when the wall moves. Cut out by 1-D interval subtraction in wall-local
 * coordinates — no polygon boolean library anywhere in this project.
 */
export interface Opening {
  id: OpeningId
  wallId: WallId
  /** Distance from node A along the wall centreline. */
  offset: Mm
  width: Mm
  kind: OpeningKind
  /** Which way a door swings; null for windows and archways. */
  flipped: boolean
  sillHeight: Mm
  height: Mm
}

export interface Furniture {
  id: FurnitureId
  kind: string
  /** Centre, in world mm. */
  p: Vec2
  rotation: Deg
  width: Mm
  depth: Mm
  layer: string
  label: string | null
}

/** Derived from closed cycles in the wall graph. Not authored directly. */
export interface DetectedRoom {
  id: RoomId
  /** Node ring, counter-clockwise, first != last. */
  ring: NodeId[]
  /** Inner polygon after wall thickness is offset inward. */
  polygon: Vec2[]
  areaMm2: number
  centroid: Vec2
  /** Holes: rings of rooms fully enclosed within this one. */
  holes: Vec2[][]
}

export interface RoomLabel {
  roomId: RoomId
  name: string
  bookable: boolean
  colour: string | null
}

export interface WifiSource {
  id: WifiSourceId
  name: string
  kind: 'router' | 'extender' | 'mesh_node'
  p: Vec2
  band: '2.4' | '5' | 'both'
  radiusStrong: Mm
  radiusUsable: Mm
  radiusEdge: Mm
  notes: string | null
}

export interface Underlay {
  imageRef: string
  /** Similarity transform from image pixels to world mm. */
  transform: { a: number; b: number; c: number; d: number; e: number; f: number }
  opacity: number
  visible: boolean
}

export interface FloorPlan {
  id: PlanId
  name: string
  nodes: PlanNode[]
  walls: Wall[]
  openings: Opening[]
  furniture: Furniture[]
  roomLabels: RoomLabel[]
  wifiSources: WifiSource[]
  underlay: Underlay | null
  /** Rotation of true north relative to plan +Y, degrees clockwise. Drives the qibla arrow. */
  northAngleDeg: Deg
  gridSize: Mm
  updatedAt: number
  version: number
}

export const EMPTY_PLAN = (id: PlanId, name: string): FloorPlan => ({
  id, name,
  nodes: [], walls: [], openings: [], furniture: [],
  roomLabels: [], wifiSources: [], underlay: null,
  northAngleDeg: 0 as Deg, gridSize: 100 as Mm,
  updatedAt: 0, version: 1,
})
